/**
 * The cover row's own rules: what order the tiles stand in, when a new one may be added, and what
 * a refused upload says.
 *
 * ── The ceiling is the SERVER's, and it is never written down here ──────────────────────────
 * `PlanCoverProcessor.MaxOwnedCovers` is 10 and arrives as `ownLimit` beside `ownCount` on every
 * `GET /plan-covers`. The phone derives its own add-tile the same way and hardcodes nothing; a 10
 * copied into this file would be a second source of truth for a number only the backend enforces,
 * and it would be the stale one the day the owner changes it.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`. `t` arrives as a parameter.
 */

import type { ConfirmPrompt } from '~/shared/ui/ConfirmDialog';

/** One tile in the cover grid. The automatic option carries no id and draws its own word. */
export interface CoverOption {
  /** `null` for the automatic pick. */
  readonly coverId: number | null;
  /** The image to draw, or `null` for the automatic option. */
  readonly imageUrl: string | null;
  /** The trainer's own upload — the only kind that may be deleted. */
  readonly isOwn: boolean;
}

/** What the pool read answers with, narrowed to what the row needs. */
export interface CoverPoolAnswer {
  readonly covers?:
    | readonly { id?: number; imageUrl?: string | null; isOwn?: boolean }[]
    | null;
  readonly ownCount?: number;
  readonly ownLimit?: number;
}

/**
 * The tiles, in the order the owner chose: the trainer's OWN first, then „auto", then the curated
 * pool.
 *
 * Own first because they are the ones the trainer went and made, and on a full pool they would
 * otherwise sit below ten pictures somebody else drew. „auto" keeps its place ahead of the curated
 * set because it is still the default — it is what every plan created before 21 September 2026
 * carries, and what a trainer who chooses nothing gets.
 *
 * Every field on the wire is optional (the generator says so), so a row missing an id or a url is
 * dropped rather than drawn as a broken tile.
 */
export function coverOptions(answer: CoverPoolAnswer | undefined): readonly CoverOption[] {
  const rows = (answer?.covers ?? [])
    .filter(
      (cover): cover is { id: number; imageUrl: string; isOwn?: boolean } =>
        typeof cover.id === 'number' && typeof cover.imageUrl === 'string',
    )
    .map((cover) => ({
      coverId: cover.id,
      imageUrl: cover.imageUrl,
      isOwn: cover.isOwn === true,
    }));

  return [
    ...rows.filter((cover) => cover.isOwn),
    { coverId: null, imageUrl: null, isOwn: false },
    ...rows.filter((cover) => !cover.isOwn),
  ];
}

/** How many of the trainer's own covers the pool holds, and the ceiling it holds them against. */
export function ownCoverCount(answer: CoverPoolAnswer | undefined): {
  readonly count: number;
  readonly limit: number;
} {
  return { count: answer?.ownCount ?? 0, limit: answer?.ownLimit ?? 0 };
}

/**
 * Whether a picture may be added right now.
 *
 * `limit > 0` guards the pending and failed reads: both answer with zeroes, and an add tile live
 * over a pool nobody has counted would upload into a ceiling it cannot see. At the ceiling the tile
 * is DIMMED rather than removed — the phone does the same — because a control that vanishes teaches
 * the trainer nothing about why, and the counter beside it already says what happened.
 */
export function canAddCover(count: number, limit: number, busy: boolean): boolean {
  return !busy && limit > 0 && count < limit;
}

/**
 * Whether a cover chosen from the old category's pool survives a category change.
 *
 * Owner rule, 4 August 2026: a curated cover belongs to its plan's category, and the pool is read
 * per category, so a row picked from Siła's is one Cardio's does not contain. An OWN upload is the
 * exception and always was — the backend stores it under a flat key and `PlanCoverKey.FitsCategory`
 * lets it fit every category, which is why it comes back in all six lists. Dropping it on a
 * category change would throw away the trainer's own picture for a rule that never applied to it.
 */
export function coverSurvivesCategoryChange(isOwn: boolean): boolean {
  return isOwn;
}

/**
 * Why an upload was refused by the SERVER, as a key.
 *
 * Branch on the code, never on the status: the media module answers 400 for three different reasons
 * and 429 for one, and only the coded ones can be worded usefully. `plan_cover_limit` is reachable
 * even with the add tile disabled — a second tab, or the phone, can fill the last slot between this
 * tab's read and its upload.
 */
export function coverUploadErrorKey(status: number, code: string | undefined): string {
  if (status === 429) return 'web:planCreate.details.coverTooMany';
  switch (code) {
    case 'plan_cover_limit':
      return 'web:planCreate.details.coverLimitReached';
    case 'media_too_large':
      return 'web:media.coverTooLarge';
    case 'media_unsupported_type':
    case 'media_content_mismatch':
      return 'web:media.coverUndecodable';
    default:
      return 'web:planCreate.details.coverUploadFailed';
  }
}

/**
 * The question asked before a cover is removed.
 *
 * Worded from the PHONE's own catalogue (`plans.builder.coverDelete*`), which this app carries
 * verbatim: the act is identical on both clients, down to what happens to the plans that used the
 * picture, so a second wording for it would be two answers to one question.
 */
export function coverDeletePrompt(translate: (key: string) => string): ConfirmPrompt {
  return {
    title: translate('plans.builder.coverDeleteTitle'),
    description: translate('plans.builder.coverDeleteBody'),
    cancelLabel: translate('common.cancel'),
    confirmLabel: translate('common.remove'),
  };
}
